import React from 'react';
import type { LiveAgent } from '../../../../../../shared/types/epics';
import { agentEmoji } from '../../../../lib/agentEmoji';
import { StatusBadge } from '../../../../components/ui';
import { formatCost } from '../format';

// FORGE-206: live in-flight agent card. Rendered only while an agent is
// actively running this story (backend surfaces `liveAgent` from the STORY-row
// heartbeat). Shows agent, phase, iteration progress, cost-so-far and the last
// tool the agent used — plus a STALE warning when the heartbeat has gone cold
// (the task likely died without writing a terminal state).
/**
 * FORGE-363: the task is launched but the agent hasn't reported in yet — a
 * Windows build instance downloads an 8–12 GB image first (~10 minutes).
 */
const StartingCard: React.FC<{ live: LiveAgent }> = ({ live }) => {
  const s = live.starting!;
  const mins = Math.floor(live.ageSeconds / 60);
  const pct = Math.min(95, Math.round((live.ageSeconds / (s.expectedMinutes * 60)) * 100));
  const label = s.platform === 'windows' ? 'Starting your Windows instance' : 'Starting the agent';
  return (
    <div
      data-testid="live-agent-starting"
      className={`mx-4 mt-3 rounded-md border px-3 py-2.5 ${live.stale ? 'border-amber-300 bg-amber-50' : 'border-sky-200 bg-sky-50'}`}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span className="relative flex h-2 w-2">
          {!live.stale && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-sky-400 opacity-75" />}
          <span className={`relative inline-flex h-2 w-2 rounded-full ${live.stale ? 'bg-amber-500' : 'bg-sky-500'}`} />
        </span>
        <span className="text-sm font-semibold text-slate-900">
          {live.stale ? `${label} — taking longer than usual` : `${label}…`}
        </span>
        <span className="font-mono text-xs text-slate-600">
          <span aria-hidden="true">{agentEmoji(live.agentType)}</span> {live.agentType}
        </span>
        <span className="ml-auto text-xs text-slate-500 tabular-nums">
          {mins < 1 ? 'just now' : `${mins} min`} · usually ~{s.expectedMinutes} min
        </span>
      </div>
      <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
        <div className={`h-full rounded-full ${live.stale ? 'bg-amber-400' : 'bg-sky-500'} transition-all`} style={{ width: `${live.stale ? 100 : pct}%` }} />
      </div>
      <div className={`mt-1.5 text-xs ${live.stale ? 'text-amber-700' : 'text-slate-600'}`}>
        {live.stale
          ? 'The instance hasn’t reported in yet. Forge alerts the team if it doesn’t start; if this card stays, comment /proceed on the issue to restart this step.'
          : s.platform === 'windows'
            ? 'This step builds .NET Framework code, so it runs on a Windows machine that downloads a large image before the agent can begin.'
            : 'The agent container is starting.'}
      </div>
    </div>
  );
};

export const LiveAgentCard: React.FC<{ live: LiveAgent }> = ({ live }) => {
  if (live.starting) return <StartingCard live={live} />;
  const pct =
    live.iter != null && live.maxIter != null && live.maxIter > 0
      ? Math.min(100, Math.round((live.iter / live.maxIter) * 100))
      : null;
  const stale = live.stale;
  const age =
    live.ageSeconds < 60
      ? `${live.ageSeconds}s ago`
      : `${Math.floor(live.ageSeconds / 60)}m ${live.ageSeconds % 60}s ago`;
  return (
    <div
      className={`mx-4 mt-3 rounded-md border px-3 py-2.5 ${
        stale ? 'border-amber-300 bg-amber-50' : 'border-sky-200 bg-sky-50'
      }`}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span className="relative flex h-2 w-2">
          {!stale && (
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-sky-400 opacity-75" />
          )}
          <span
            className={`relative inline-flex h-2 w-2 rounded-full ${
              stale ? 'bg-amber-500' : 'bg-sky-500'
            }`}
          />
        </span>
        <span className="text-sm font-semibold text-slate-900">
          {stale ? 'Agent may be stuck' : 'Agent running'}
        </span>
        <span className="font-mono text-xs text-slate-600">
          <span aria-hidden="true">{agentEmoji(live.agentType)}</span> {live.agentType}
        </span>
        <StatusBadge tone={stale ? 'warning' : 'info'}>{live.phase}</StatusBadge>
        {stale && (
          <StatusBadge tone="warning">STALE · no heartbeat {age}</StatusBadge>
        )}
        <span className="ml-auto text-xs text-slate-500">updated {age}</span>
      </div>

      <div className="mt-2 flex items-center gap-3 text-xs text-slate-700">
        {live.iter != null && (
          <span className="font-mono tabular-nums">
            iter {live.iter}
            {live.maxIter != null ? ` / ${live.maxIter}` : ''}
          </span>
        )}
        {live.costUsd != null && (
          <span className="font-mono tabular-nums">{formatCost(live.costUsd)} so far</span>
        )}
      </div>

      {pct != null && (
        <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
          <div
            className={`h-full rounded-full ${stale ? 'bg-amber-400' : 'bg-sky-500'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}

      {live.lastTool && (
        <div className="mt-1.5 truncate font-mono text-xs text-slate-500" title={live.lastTool}>
          ▸ {live.lastTool}
        </div>
      )}
      {stale && (
        <div className="mt-1.5 text-xs text-amber-700">
          No heartbeat for {age}. The task may have died — it will be recovered
          automatically, or you can Abort and retry this story.
        </div>
      )}
    </div>
  );
};
